// Localização: frontend/src/components/Prontuario/PacienteResponsavelTab.jsx
// Aba de Responsável Legal e Financeiro do Prontuário do Paciente

import React, { useState, useEffect } from 'react';
import Card from '../UI/Card';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';
import Alert from '../UI/Alert';
import LoadingIndicator from '../Common/LoadingIndicator';
import ConfirmacaoModal from '../Common/ConfirmacaoModal';
import { useNotification } from '../../context/NotificationContext';
import { User, Phone, Mail, MapPin, FileText, Edit, Save, X, Building2, CreditCard, UserCheck, Plus, Trash2 } from 'lucide-react';
import api from '../../api/api';

const PacienteResponsavelTab = ({ pacienteId }) => {
    const { success, error: showError } = useNotification();
    const [responsaveis, setResponsaveis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [editingResponsavel, setEditingResponsavel] = useState(null);
    const [deletingResponsavel, setDeletingResponsavel] = useState(null);
    const [formData, setFormData] = useState({
        nome_completo: '',
        cpf: '',
        rg: '',
        data_nascimento: '',
        telefone: '',
        celular: '',
        email: '',
        endereco: '',
        cidade: '',
        estado: '',
        cep: '',
        parentesco: '',
        tipo_responsabilidade: 'legal', // 'legal', 'financeiro', 'ambos'
        observacoes: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchResponsaveis();
    }, [pacienteId]);

    const fetchResponsaveis = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/pacientes/${pacienteId}/responsavel`);
            setResponsaveis(response.data || []);
        } catch (err) {
            // Se não encontrar (404), não é erro - pode não ter responsáveis cadastrados
            if (err.response?.status === 404) {
                setResponsaveis([]);
            } else {
                console.error('Erro ao buscar responsáveis:', err);
                showError('Erro ao carregar informações dos responsáveis.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (responsavelToEdit = null) => {
        setIsCreatingNew(!responsavelToEdit);
        setEditingResponsavel(responsavelToEdit);
        if (responsavelToEdit) {
            setFormData(responsavelToEdit);
        } else {
            setFormData({
                nome_completo: '',
                cpf: '',
                rg: '',
                data_nascimento: '',
                telefone: '',
                celular: '',
                email: '',
                endereco: '',
                cidade: '',
                estado: '',
                cep: '',
                parentesco: '',
                tipo_responsabilidade: 'legal',
                observacoes: ''
            });
        }
        setErrors({});
        setIsModalOpen(true);
    };

    // Fechar modal sem limpar dados (backdrop/ESC)
    const handleCloseModal = () => {
        setIsModalOpen(false);
        // Não limpa os dados, apenas fecha o modal
    };

    // Fechar modal e limpar dados (botão cancelar ou X)
    const handleCancelModal = () => {
        setIsModalOpen(false);
        setIsCreatingNew(false);
        setEditingResponsavel(null);
        setErrors({});
        // Limpa o formulário
        setFormData({
            nome_completo: '',
            cpf: '',
            rg: '',
            data_nascimento: '',
            telefone: '',
            celular: '',
            email: '',
            endereco: '',
            cidade: '',
            estado: '',
            cep: '',
            parentesco: '',
            tipo_responsabilidade: 'legal',
            observacoes: ''
        });
    };

    const handleOpenDeleteModal = (responsavel) => {
        setDeletingResponsavel(responsavel);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setDeletingResponsavel(null);
    };

    const handleDeleteResponsavel = async () => {
        if (!deletingResponsavel) return;

        setDeleting(true);
        try {
            await api.delete(`/pacientes/${pacienteId}/responsavel/${deletingResponsavel.id}`);
            success('Responsável removido com sucesso!');
            await fetchResponsaveis();
            setIsDeleteModalOpen(false);
            setDeletingResponsavel(null);
        } catch (err) {
            console.error('Erro ao deletar responsável:', err);
            showError(err.response?.data?.message || 'Erro ao remover responsável. Tente novamente.');
        } finally {
            setDeleting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Limpar erro do campo quando o usuário começar a digitar
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.nome_completo || formData.nome_completo.trim() === '') {
            newErrors.nome_completo = 'Nome completo é obrigatório';
        }

        if (formData.cpf && !/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(formData.cpf) && formData.cpf.length > 0) {
            // Permitir apenas números e formatar depois
            const cpfNumbers = formData.cpf.replace(/\D/g, '');
            if (cpfNumbers.length !== 11) {
                newErrors.cpf = 'CPF deve ter 11 dígitos';
            }
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Email inválido';
        }

        if (formData.cep && formData.cep.replace(/\D/g, '').length !== 8) {
            newErrors.cep = 'CEP deve ter 8 dígitos';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const formatCPF = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        return value;
    };

    const formatCEP = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 8) {
            return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
        }
        return value;
    };

    const handleCPFChange = (e) => {
        const formatted = formatCPF(e.target.value);
        setFormData(prev => ({ ...prev, cpf: formatted }));
    };

    const handleCEPChange = (e) => {
        const formatted = formatCEP(e.target.value);
        setFormData(prev => ({ ...prev, cep: formatted }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSaving(true);
        try {
            if (editingResponsavel) {
                // Atualizar responsável existente
                await api.put(`/pacientes/${pacienteId}/responsavel/${editingResponsavel.id}`, formData);
                success('Responsável atualizado com sucesso!');
            } else {
                // Criar novo responsável
                await api.post(`/pacientes/${pacienteId}/responsavel`, formData);
                success('Responsável cadastrado com sucesso!');
            }
            await fetchResponsaveis();
            handleCancelModal();
        } catch (err) {
            console.error('Erro ao salvar responsável:', err);
            showError(err.response?.data?.message || 'Erro ao salvar responsável. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <LoadingIndicator message="Carregando informações dos responsáveis..." />;
    }

    const ResponsaveisList = () => {
        if (responsaveis.length === 0) {
            return (
                <Card>
                    <div className="text-center py-16 px-6">
                        <UserCheck className="w-16 h-16 mx-auto text-gray-400 mb-5" />
                        <p className="text-gray-600 text-lg font-medium mb-3">
                            Nenhum responsável cadastrado
                        </p>
                        <p className="text-gray-500 text-sm mb-8">
                            Adicione as informações dos responsáveis legais e/ou financeiros do paciente.
                        </p>
                        <PrimaryButton onClick={() => handleOpenModal(null)} className="flex items-center gap-2 mx-auto">
                            <Plus className="w-4 h-4" />
                            Adicionar Responsável
                        </PrimaryButton>
                    </div>
                </Card>
            );
        }

        return (
            <div className="space-y-4">
                <div className="flex justify-end items-center mb-6">
                    <PrimaryButton onClick={() => handleOpenModal(null)} className="flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Adicionar Novo
                    </PrimaryButton>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {responsaveis.map((responsavel) => (
                        <ResponsavelCard key={responsavel.id} responsavel={responsavel} />
                    ))}
                </div>
            </div>
        );
    };

    const ResponsavelCard = ({ responsavel }) => {

        return (
            <Card>
                <div className="p-6">
                    <div className="flex justify-between items-start mb-8 pb-6 border-b border-gray-200">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-emerald-100 rounded-lg">
                                <UserCheck className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 mb-2">{responsavel.nome_completo}</h2>
                                <div className="flex gap-2 flex-wrap">
                                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                        responsavel.tipo_responsabilidade === 'legal'
                                            ? 'bg-blue-100 text-blue-800'
                                            : responsavel.tipo_responsabilidade === 'financeiro'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-purple-100 text-purple-800'
                                    }`}>
                                        {responsavel.tipo_responsabilidade === 'legal'
                                            ? 'Legal'
                                            : responsavel.tipo_responsabilidade === 'financeiro'
                                            ? 'Financeiro'
                                            : 'Legal e Financeiro'}
                                    </span>
                                    {responsavel.parentesco && (
                                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                            {responsavel.parentesco}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleOpenModal(responsavel)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Editar responsável"
                            >
                                <Edit className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleOpenDeleteModal(responsavel)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remover responsável"
                            >
                                <Trash2 className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Informações Pessoais */}
                        <div className="space-y-5">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2 pb-2 border-b border-gray-100">
                                <User className="w-4 h-4" />
                                Informações Pessoais
                            </h3>
                            <div className="space-y-4 pl-1">
                                {responsavel.cpf && (
                                    <div className="py-1">
                                        <span className="text-xs text-gray-500 block mb-1">CPF</span>
                                        <p className="text-sm font-medium text-gray-900">{responsavel.cpf}</p>
                                    </div>
                                )}
                                {responsavel.rg && (
                                    <div className="py-1">
                                        <span className="text-xs text-gray-500 block mb-1">RG</span>
                                        <p className="text-sm font-medium text-gray-900">{responsavel.rg}</p>
                                    </div>
                                )}
                                {responsavel.data_nascimento && (
                                    <div className="py-1">
                                        <span className="text-xs text-gray-500 block mb-1">Data de Nascimento</span>
                                        <p className="text-sm font-medium text-gray-900">
                                            {new Date(responsavel.data_nascimento).toLocaleDateString('pt-BR')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Contato */}
                        <div className="space-y-5">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2 pb-2 border-b border-gray-100">
                                <Phone className="w-4 h-4" />
                                Contato
                            </h3>
                            <div className="space-y-4 pl-1">
                                {responsavel.telefone && (
                                    <div className="py-1">
                                        <span className="text-xs text-gray-500 block mb-1">Telefone</span>
                                        <p className="text-sm font-medium text-gray-900">{responsavel.telefone}</p>
                                    </div>
                                )}
                                {responsavel.celular && (
                                    <div className="py-1">
                                        <span className="text-xs text-gray-500 block mb-1">Celular</span>
                                        <p className="text-sm font-medium text-gray-900">{responsavel.celular}</p>
                                    </div>
                                )}
                                {responsavel.email && (
                                    <div className="py-1">
                                        <span className="text-xs text-gray-500 block mb-1">Email</span>
                                        <p className="text-sm font-medium text-gray-900">{responsavel.email}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Endereço */}
                        {(responsavel.endereco || responsavel.cidade || responsavel.estado || responsavel.cep) && (
                            <div className="space-y-5 md:col-span-2 pt-4 border-t border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2 pb-2 border-b border-gray-100">
                                    <MapPin className="w-4 h-4" />
                                    Endereço
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-1">
                                    {responsavel.endereco && (
                                        <div className="py-1">
                                            <span className="text-xs text-gray-500 block mb-1">Endereço</span>
                                            <p className="text-sm font-medium text-gray-900">{responsavel.endereco}</p>
                                        </div>
                                    )}
                                    {responsavel.cidade && (
                                        <div className="py-1">
                                            <span className="text-xs text-gray-500 block mb-1">Cidade</span>
                                            <p className="text-sm font-medium text-gray-900">{responsavel.cidade}</p>
                                        </div>
                                    )}
                                    {responsavel.estado && (
                                        <div className="py-1">
                                            <span className="text-xs text-gray-500 block mb-1">Estado</span>
                                            <p className="text-sm font-medium text-gray-900">{responsavel.estado}</p>
                                        </div>
                                    )}
                                    {responsavel.cep && (
                                        <div className="py-1">
                                            <span className="text-xs text-gray-500 block mb-1">CEP</span>
                                            <p className="text-sm font-medium text-gray-900">{responsavel.cep}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Observações */}
                        {responsavel.observacoes && (
                            <div className="md:col-span-2 space-y-3 pt-4 border-t border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2 pb-2 border-b border-gray-100">
                                    <FileText className="w-4 h-4" />
                                    Observações
                                </h3>
                                <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg ml-1">
                                    {responsavel.observacoes}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        );
    };

    return (
        <div className="space-y-6">
            <ResponsaveisList />

            {/* Modal de Formulário */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onCloseX={handleCancelModal}
                title={editingResponsavel ? 'Editar Responsável' : 'Adicionar Responsável'}
                size="xl"
            >
                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Tipo de Responsabilidade */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tipo de Responsabilidade <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="tipo_responsabilidade"
                            value={formData.tipo_responsabilidade}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            required
                        >
                            <option value="legal">Legal</option>
                            <option value="financeiro">Financeiro</option>
                            <option value="ambos">Legal e Financeiro</option>
                        </select>
                    </div>

                    {/* Nome Completo */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nome Completo <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="nome_completo"
                            value={formData.nome_completo}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                                errors.nome_completo ? 'border-red-500' : 'border-gray-300'
                            }`}
                            required
                        />
                        {errors.nome_completo && (
                            <p className="mt-1 text-sm text-red-500">{errors.nome_completo}</p>
                        )}
                    </div>

                    {/* CPF e RG */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                CPF
                            </label>
                            <input
                                type="text"
                                name="cpf"
                                value={formData.cpf}
                                onChange={handleCPFChange}
                                placeholder="000.000.000-00"
                                maxLength="14"
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                                    errors.cpf ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                            {errors.cpf && (
                                <p className="mt-1 text-sm text-red-500">{errors.cpf}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                RG
                            </label>
                            <input
                                type="text"
                                name="rg"
                                value={formData.rg}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                    </div>

                    {/* Data de Nascimento e Parentesco */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Data de Nascimento
                            </label>
                            <input
                                type="date"
                                name="data_nascimento"
                                value={formData.data_nascimento}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Parentesco
                            </label>
                            <input
                                type="text"
                                name="parentesco"
                                value={formData.parentesco}
                                onChange={handleChange}
                                placeholder="Ex: Pai, Mãe, Filho(a), etc."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                    </div>

                    {/* Telefone e Celular */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Telefone
                            </label>
                            <input
                                type="tel"
                                name="telefone"
                                value={formData.telefone}
                                onChange={handleChange}
                                placeholder="(00) 0000-0000"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Celular
                            </label>
                            <input
                                type="tel"
                                name="celular"
                                value={formData.celular}
                                onChange={handleChange}
                                placeholder="(00) 00000-0000"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                                errors.email ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {errors.email && (
                            <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                        )}
                    </div>

                    {/* Endereço */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Endereço
                        </label>
                        <input
                            type="text"
                            name="endereco"
                            value={formData.endereco}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                    </div>

                    {/* Cidade, Estado e CEP */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Cidade
                            </label>
                            <input
                                type="text"
                                name="cidade"
                                value={formData.cidade}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Estado
                            </label>
                            <input
                                type="text"
                                name="estado"
                                value={formData.estado}
                                onChange={handleChange}
                                placeholder="UF"
                                maxLength="2"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            CEP
                        </label>
                        <input
                            type="text"
                            name="cep"
                            value={formData.cep}
                            onChange={handleCEPChange}
                            placeholder="00000-000"
                            maxLength="9"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                                errors.cep ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {errors.cep && (
                            <p className="mt-1 text-sm text-red-500">{errors.cep}</p>
                        )}
                    </div>

                    {/* Observações */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Observações
                        </label>
                        <textarea
                            name="observacoes"
                            value={formData.observacoes}
                            onChange={handleChange}
                            rows="4"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="Informações adicionais sobre o responsável..."
                        />
                    </div>

                    {/* Botões */}
                    <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleCancelModal}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                            <X className="w-4 h-4" />
                            Cancelar
                        </button>
                        <PrimaryButton
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <LoadingIndicator size="sm" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {editingResponsavel ? 'Atualizar' : 'Salvar'}
                                </>
                            )}
                        </PrimaryButton>
                    </div>
                </form>
            </Modal>

            {/* Modal de Confirmação de Exclusão */}
            {deletingResponsavel && (
                <ConfirmacaoModal
                    isOpen={isDeleteModalOpen}
                    onClose={handleCloseDeleteModal}
                    onConfirm={handleDeleteResponsavel}
                    titulo="Remover Responsável"
                    mensagem={`Tem certeza que deseja remover o responsável <strong>${deletingResponsavel.nome_completo}</strong>? Esta ação não pode ser desfeita.`}
                    confirmText={deleting ? "Removendo..." : "Remover"}
                    cancelText="Cancelar"
                />
            )}
        </div>
    );
};

export default PacienteResponsavelTab;

